import { isNavigationFailure, useRouter } from 'vue-router'

import axios from 'axios'

const config = { isEnabled: true }

const state = {
  userId: null,
  events: [],
  consentAllowed: null,
  isDebugEnabled: false,
}

// Axios local instance.
const iterableApi = axios.create({
  baseURL: 'https://api.eu.iterable.com',
})

const iterableEndpoints = {
  trackEvent: '/api/events/track',
}

function setApiKey (apiKey) {
  iterableApi.defaults.headers.common['api-key'] = apiKey
}

// Add iterable-debug=enabled as a URL parameter to test Iterable events.
function isDebugEnabled (debugAvailability) {
  if (typeof window === 'undefined') return false
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.has('iterable-debug') && debugAvailability) {
    const value = urlParams.get('iterable-debug')?.toLowerCase()
    return value === 'enabled'
  }

  return false
}

// Check for Iterable consent using consent events from cookiebot.
// Null is needed to ensure the cookies consent even if the cookiebot does not load.
function checkIterableConsent () {
  if (typeof window === 'undefined' || !window.dataLayer || !Array.isArray(window.dataLayer)) {
    return null
  }
  // Check if last consent_status event exists and identify Iterable consent.
  for (let i = window.dataLayer.length - 1; i >= 0; i--) {
    const item = window.dataLayer[i]

    if (item && typeof item === 'object' && item.event === 'consent_status') {
      return item.Iterable
    }
  }

  return null
}

async function track (eventName, data = {}) {
  if (!eventName) throw new Error('An event name should be provided.')

  const { isEnabled } = config
  const { eventId, userId, dataFields } = data

  if (!isEnabled) return

  state.consentAllowed = checkIterableConsent()

  // We only block tracking when consent is explicitly false.
  if (state.consentAllowed === false) {
    if (state.isDebugEnabled) console.info('❌ Iterable tracking blocked: Consent explicitly denied.')
    return
  }

  if (state.consentAllowed === null && state.isDebugEnabled) {
    console.info('ℹ️ Iterable tracking allowed: No explicit consent information found, proceeding by default.')
  }

  const eventData = {
    eventName,
    eventId,
    dataFields,
    createdAt: Math.floor(Date.now() / 1000), // Unix timestamp.
  }

  state.events.push(eventData)

  if (userId) {
    state.userId = userId

    while (state.events.length > 0) {
      const event = state.events.shift()
      try {
        if (state.isDebugEnabled) {
          console.group('📘 Iterable Tracking Event:', event.eventName)
          console.info('Event Data:', event.dataFields ? event.dataFields : 'not available')
          console.info('Target URL:', iterableEndpoints.trackEvent)
          console.groupEnd()
        }

        await iterableApi.post(iterableEndpoints.trackEvent, {
          ...event,
          userId,
        })

        if (state.isDebugEnabled) console.info('✅ Iterable Event sent successfully:', event.eventName)

        await new Promise(resolve => setTimeout(resolve, 250))
      } catch (err) {
        console.error(err)
      }
    }
  }
}

function trackPageViews () {
  const router = useRouter()
  if (!router || typeof router.afterEach !== 'function') {
    if (state.isDebugEnabled) console.warn('📧 Router.afterEach not available for Iterable page tracking')
    return
  }

  router.afterEach((to, from, failure) => {
    if (isNavigationFailure(failure)) return

    if (config.excludedPathNames?.some(pathName => RegExp(pathName).test(to.name))) return

    const pageLocation = location ? location.href : `${config.baseURL || ''}${to.fullPath}`

    track('page_view', {
      userId: state.userId,
      dataFields: {
        pageName: to.name,
        pageLocation,
      },
    })
  })
}

function installPlugin (app, options = {}) {
  state.isDebugEnabled = isDebugEnabled(options.debug)

  const { apiKey, ...otherOptions } = options
  if (!apiKey) throw new Error('Iterable Api-Key is required')

  setApiKey(apiKey)

  Object.assign(config, { ...otherOptions })

  state.consentAllowed = checkIterableConsent()

  if (state.isDebugEnabled) {
    console.info('📧 Iterable Consent Status:', state.consentAllowed ? 'Granted' : 'Denied')
  }

  if (config.pageTracking) {
    trackPageViews()
    if (state.isDebugEnabled) {
      console.warn('📧 Router not available for Iterable page tracking')
    }
  }

  app.provide('iterable', this)
}

export default {
  install: installPlugin,
  track,
}
